/**
 * Tokens live in `localStorage` keyed under `xop.*`. localStorage is fine
 * for our threat model:
 *   - Same-origin only (the dashboard web app and its Vite dev server).
 *   - The dashboard server *also* sets HttpOnly cookies, so an XSS on a
 *     completely different origin can't hijack us.
 *   - The Tauri shell uses Tauri's secure store via the auth bridge —
 *     same getter / setter API.
 */

const ACCESS_KEY = "xop.access_token";
const REFRESH_KEY = "xop.refresh_token";

/**
 * The CRM session (under `src/crm/`) lives at three localStorage keys:
 *   xop_crm_token  — a brain-crm-api JWT (different system, different
 *                    audience than the dashboard's access token)
 *   xop_crm_user   — JSON, populated by CrmRoot's bootstrap login
 *   xop_crm_tenant — JSON, ditto
 *
 * The CRM session bootstrap (apps/web/src/crm/CrmRoot.tsx) is the
 * SOLE writer of these keys. We deliberately do NOT push the
 * dashboard's access token into xop_crm_token — they are JWTs for
 * different systems, and clobbering the brain JWT with the
 * dashboard's would make every brain API call 401 → the CRM's
 * axios interceptor (lib/api.ts) hard-redirects to '/' on 401, so
 * the user would experience "click /crm → page reloads back home".
 *
 * What we DO coordinate is logout: when the dashboard signs out,
 * `clearCrmSession()` is called from `tokenStore.clear()` so the
 * CRM session is cleared at the same time and a fresh /crm visit
 * triggers a fresh bootstrap.
 */
function clearCrmSession(): void {
  if (!isStorageAvailable()) return;
  window.localStorage.removeItem("xop_crm_token");
  window.localStorage.removeItem("xop_crm_user");
  window.localStorage.removeItem("xop_crm_tenant");
}

/**
 * Probe whether `window.localStorage` is usable (it throws in private
 * mode / when disabled). Exported so sibling stores (e.g. `lastEmail`)
 * reuse the same guard instead of duplicating it.
 */
export function isStorageAvailable(): boolean {
  try {
    const k = "__xop_test__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

let memoryAccess: string | null = null;
let memoryRefresh: string | null = null;

export const tokenStore = {
  getAccess(): string | null {
    if (!isStorageAvailable()) return memoryAccess;
    return window.localStorage.getItem(ACCESS_KEY);
  },
  getRefresh(): string | null {
    if (!isStorageAvailable()) return memoryRefresh;
    return window.localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string): void {
    if (!isStorageAvailable()) {
      memoryAccess = access;
      memoryRefresh = refresh;
      return;
    }
    window.localStorage.setItem(ACCESS_KEY, access);
    window.localStorage.setItem(REFRESH_KEY, refresh);
  },
  setAccess(access: string): void {
    if (!isStorageAvailable()) {
      memoryAccess = access;
      return;
    }
    window.localStorage.setItem(ACCESS_KEY, access);
  },
  clear(): void {
    if (!isStorageAvailable()) {
      memoryAccess = null;
      memoryRefresh = null;
      clearCrmSession();
      return;
    }
    window.localStorage.removeItem(ACCESS_KEY);
    window.localStorage.removeItem(REFRESH_KEY);
    clearCrmSession();
  },
};
